import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { Receipt, CreditCard, Loader2, AlertTriangle, RefreshCw, XCircle } from 'lucide-react';
import { isIOSNative } from '../../utils/nativeBridge';

/**
 * Shared Backlog Fee Confirmation Dialog.
 * Handles both the initial confirmation and payment failure retry/rollback.
 */
export function BacklogConfirmDialog({
  backlogPreview,
  confirming,
  onConfirm,
  onDecline,
  // Payment failure props
  paymentFailure,
  onRetryNow,
  onRetryLater,
  retrying,
  rollingBack,
}) {
  if (!backlogPreview && !paymentFailure) return null;

  // iOS App Store build: the fee-confirmation dialog must never render
  // (no purchase UI in-app). Paid-choice users complete this on the web;
  // free-choice businesses never reach this state (server-side free path).
  if (isIOSNative()) return null;
  
  // Payment Failure View
  if (paymentFailure) {
    return (
      <Dialog open={true} onOpenChange={() => {}}>
        <DialogContent className="sm:max-w-md" data-testid="backlog-payment-failure-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-700">
              <AlertTriangle className="w-5 h-5" />
              Payment Failed
            </DialogTitle>
            <DialogDescription>
              We couldn't process your payment. Please choose how to proceed.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <Alert className="border-red-200 bg-red-50">
              <XCircle className="h-4 w-4 text-red-600" />
              <AlertDescription className="text-red-800">
                {paymentFailure.reason}
              </AlertDescription>
            </Alert>
            
            {paymentFailure.total_fee && (
              <div className="p-3 bg-muted rounded-lg">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Amount attempted</span>
                  <span className="font-semibold">
                    {paymentFailure.currency === 'NGN' ? '\u20A6' : '$'}
                    {paymentFailure.total_fee?.toLocaleString()}
                  </span>
                </div>
                <div className="flex justify-between text-sm mt-1">
                  <span className="text-muted-foreground">Transactions</span>
                  <span>{paymentFailure.backlog_tx_count}</span>
                </div>
              </div>
            )}

            <div className="space-y-3 pt-2">
              <div className="p-3 border rounded-lg">
                <p className="text-sm font-medium mb-1">Retry Now</p>
                <p className="text-xs text-muted-foreground">
                  Re-fetch transactions from your bank (in case new ones were added) and attempt payment again.
                </p>
              </div>
              <div className="p-3 border rounded-lg">
                <p className="text-sm font-medium mb-1">Retry Later</p>
                <p className="text-xs text-muted-foreground">
                  This will remove the pending connection. You'll need to go through the bank connection process again when you're ready.
                </p>
              </div>
            </div>
          </div>
          <DialogFooter className="flex gap-2 sm:gap-0">
            <Button
              variant="outline"
              onClick={onRetryLater}
              disabled={retrying || rollingBack}
              data-testid="retry-later-btn"
            >
              {rollingBack ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Removing...
                </>
              ) : (
                'Retry Later'
              )}
            </Button>
            <Button
              onClick={onRetryNow}
              disabled={retrying || rollingBack}
              className="bg-[#108981] hover:bg-[#108981]/90"
              data-testid="retry-now-btn"
            >
              {retrying ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Retrying...
                </>
              ) : (
                <>
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Retry Now
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }
  
  // Normal Confirmation View
  const providerLabel = backlogPreview.provider === 'mono'
    ? 'Mono' : (backlogPreview.provider === 'plaid' ? 'Plaid' : null);
  const providerBody = backlogPreview.provider === 'mono'
    ? 'Mono needs to import your historical bank transactions. Your card on file will be charged, then the transactions will be classified and posted to your ledger.'
    : 'This fee covers fetching, categorizing, and posting all ' + backlogPreview.transaction_count + ' historical transactions to your ledger. Current month\u2019s transactions are free. Bank charges are excluded. Your card on file will be charged.';

  const symbol = backlogPreview.symbol || (backlogPreview.currency === 'NGN' ? '\u20A6' : backlogPreview.currency === 'GBP' ? '\u00A3' : '$');
  const freeAvail = Number(backlogPreview.backlog_free_available ?? backlogPreview.free_available ?? 0);
  const freeCons = Number(backlogPreview.backlog_free_consumed ?? backlogPreview.free_consumed ?? 0);
  const chargeable = Number(backlogPreview.backlog_chargeable ?? backlogPreview.chargeable ?? backlogPreview.transaction_count);
  const minimumFee = Number(backlogPreview.backlog_minimum_fee ?? backlogPreview.minimum_fee ?? 0);
  // iter1238 Phase 8.13n — universal 3-bucket breakdown for Mono/Plaid.
  // These fields are present when the backend recorded them (banking.py
  // and open_banking_feeds.py both now stamp them on the connection).
  const excludedCharges = Number(backlogPreview.backlog_excluded_bank_charge_count ?? 0);
  const postRegistration = Number(backlogPreview.backlog_post_registration_count ?? 0);
  const totalInWindow = (Number(backlogPreview.transaction_count) || 0) + excludedCharges;
  const windowStart = backlogPreview.backlog_window_start;
  const windowEnd = backlogPreview.backlog_window_end;

  return (
    <Dialog open={!!backlogPreview} onOpenChange={(open) => { if (!open) onDecline(); }}>
      <DialogContent className="sm:max-w-md" data-testid="backlog-confirmation-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Receipt className="w-5 h-5 text-[#108981]" />
            Historical Transaction Fee
            {providerLabel && (
              <span
                className="ml-2 text-[10px] font-medium uppercase tracking-wide bg-slate-100 text-slate-600 px-2 py-0.5 rounded"
                data-testid={`backlog-provider-badge-${backlogPreview.provider}`}
              >
                {providerLabel}
              </span>
            )}
          </DialogTitle>
          <DialogDescription>
            {backlogPreview.institution_name
              ? `${backlogPreview.institution_name} needs your approval to import prior-month transactions.`
              : 'Your accounting start month requires fetching historical transactions from your bank.'}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="p-4 bg-muted rounded-lg space-y-2" data-testid="backlog-fee-breakdown">
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">
                Backlog transactions
                {windowStart && windowEnd && (
                  <span className="ml-1 text-[11px] text-slate-400">
                    ({String(windowStart).slice(0, 10)} → {String(windowEnd).slice(0, 10)})
                  </span>
                )}
              </span>
              <span className="font-semibold" data-testid="backlog-tx-count">{totalInWindow || backlogPreview.transaction_count}</span>
            </div>
            {excludedCharges > 0 && (
              <div className="flex justify-between text-sm pl-4" data-testid="backlog-excluded-charges-row">
                <span className="text-muted-foreground">
                  — Bank charges (free)
                  <span className="ml-1 text-[11px] text-slate-400">excluded from backlog fee</span>
                </span>
                <span className="font-semibold" data-testid="backlog-excluded-charges-count">{excludedCharges}</span>
              </div>
            )}
            <div className="flex justify-between text-sm pl-4" data-testid="backlog-chargeable-row">
              <span className="text-muted-foreground">— Chargeable<span className="ml-1 text-[11px] text-slate-400">counted for the fee</span></span>
              <span className="font-semibold" data-testid="backlog-chargeable-count">{chargeable}</span>
            </div>
            {postRegistration > 0 && (
              <div className="flex justify-between text-sm" data-testid="backlog-post-registration-row">
                <span className="text-muted-foreground">
                  Current transactions (free)
                  <span className="ml-1 text-[11px] text-slate-400">from registration month onward</span>
                </span>
                <span className="font-semibold" data-testid="backlog-post-registration-count">{postRegistration}</span>
              </div>
            )}
            {freeCons > 0 && (
              <div className="flex justify-between text-sm" data-testid="backlog-free-consumed-row">
                <span className="text-muted-foreground">
                  Covered by your free lifetime allowance
                  {freeAvail > 0 && (
                    <span className="ml-1 text-[11px] text-slate-400">({freeAvail} available)</span>
                  )}
                </span>
                <span className="text-emerald-700">- {freeCons}</span>
              </div>
            )}
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Rate per transaction</span>
              <span>{symbol}{backlogPreview.per_transaction_rate}</span>
            </div>
            {minimumFee > 0 && (
              <div className="flex justify-between text-[11px] text-slate-400">
                <span>Minimum charge (per bank)</span>
                <span>{symbol}{minimumFee}</span>
              </div>
            )}
            <div className="border-t pt-2 flex justify-between font-semibold">
              <span>Total fee</span>
              {backlogPreview.fee_waived ? (
                <span className="text-[#108981]" data-testid="backlog-fee-waived">
                  <span className="line-through text-muted-foreground mr-2 font-normal">
                    {symbol}{backlogPreview.total_fee?.toLocaleString()}
                  </span>
                  Waived
                </span>
              ) : (
                <span className="text-[#108981]" data-testid="backlog-total-fee">{symbol}{backlogPreview.total_fee?.toLocaleString()}</span>
              )}
            </div>
          </div>
          {backlogPreview.fee_waived ? (
            <Alert className="border-emerald-200 bg-emerald-50">
              <AlertDescription className="text-emerald-800 text-sm">
                This fee falls below your payment processor's minimum charge, so
                we're waiving it. Your card will not be charged. Your {backlogPreview.transaction_count}{' '}
                historical transaction{backlogPreview.transaction_count === 1 ? '' : 's'}{' '}
                will still be fetched and posted to your ledger.
              </AlertDescription>
            </Alert>
          ) : (
            <p className="text-sm text-muted-foreground">
              {providerBody}
            </p>
          )}
        </div>
        <DialogFooter className="flex gap-2 sm:gap-0">
          <Button
            variant="outline"
            onClick={onDecline}
            disabled={confirming}
            data-testid="decline-backlog-btn"
          >
            Not now
          </Button>
          <Button
            onClick={onConfirm}
            disabled={confirming}
            className="bg-[#108981] hover:bg-[#108981]/90"
            data-testid="confirm-backlog-btn"
          >
            {confirming ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Processing...
              </>
            ) : (
              <>
                <CreditCard className="w-4 h-4 mr-2" />
                {backlogPreview.fee_waived
                  ? `Confirm — covered by free allowance (${backlogPreview.currency_symbol || backlogPreview.currency || ''}0)`
                  : 'Confirm & Connect'}
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
